import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Description as DocumentIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ManageInterviewModal from './ManageInterviewModal';
import ViewMeetingModal from './ViewMeetingModal';
import InterviewProgressSteps from './InterviewProgressSteps';

interface Application {
  id: string;
  title?: string;
  letter?: string;
  status: string;
  ai_application?: boolean; // Indicates if this application was generated by AI
  created_at: string;
  updated_at: string;
  product?: {
    id: string;
    name: string;
    images?: string[];
    company_id?: string;
  };
  applicant?: {
    id: string;
    alias?: string;
    first_name?: string;
    last_name?: string;
  };
  meeting?: {
    id: string;
    interviewType: 'in_person' | 'video' | 'phone';
    date: string;
    time: string;
    timezone: string;
    location?: string;
    videoLink?: string;
    phoneNumber?: string;
    notes?: string;
    status?: 'scheduled' | 'completed' | 'cancelled';
    createdAt?: string;
    updatedAt?: string;
  };
}

interface CompanyAcceptedApplicationsProps {
  applications: Application[];
  onViewApplication?: (application: Application) => void;
  onContactApplicant?: (application: Application) => void;
  onViewPosting?: (application: Application) => void;
  onRefreshApplications?: () => void;
  loading?: boolean;
}

const CompanyAcceptedApplications: React.FC<CompanyAcceptedApplicationsProps> = ({
  applications,
  onViewApplication,
  onContactApplicant,
  onViewPosting,
  onRefreshApplications,
  loading = false,
}) => {
  const theme = useTheme();
  const [manageOpenForId, setManageOpenForId] = useState<string | null>(null);
  const [viewMeetingOpenForId, setViewMeetingOpenForId] = useState<string | null>(null);
  const [applicationsData, setApplicationsData] = useState(applications);

  // Sync applications data when props change
  useEffect(() => {
    setApplicationsData(applications);
  }, [applications]);

  // Refresh application data after meeting creation
  const handleMeetingCreated = async () => {
    setManageOpenForId(null);
    // Call the parent's refresh function to reload applications with meeting data
    onRefreshApplications?.();
  };

  // Interview steps data - shows actual progress based on meeting status
  const getInterviewSteps = (application: Application) => {
    const hasMeeting = !!application.meeting;
    const meetingType = application.meeting?.interviewType;
    
    return [
      {
        id: 'initial-review',
        label: 'Initial Review',
        description: 'Application reviewed and accepted',
        completed: true,
        active: false,
      },
      {
        id: 'interview-scheduling',
        label: hasMeeting ? `${meetingType === 'video' ? 'Video' : meetingType === 'phone' ? 'Phone' : 'In-Person'} Interview` : 'Schedule Interview',
        description: hasMeeting 
          ? `${meetingType === 'video' ? 'Video call' : meetingType === 'phone' ? 'Phone call' : 'In-person meeting'} scheduled`
          : 'Schedule the first interview with the candidate',
        completed: hasMeeting,
        active: !hasMeeting,
        meetingData: application.meeting,
      },
      {
        id: 'technical-interview',
        label: 'Technical Interview',
        description: hasMeeting ? 'Technical skills assessment' : 'Schedule technical assessment',
        completed: false,
        active: false,
      },
      {
        id: 'final-interview',
        label: 'Final Interview',
        description: hasMeeting ? 'Final round with hiring manager' : 'Schedule final interview',
        completed: false,
        active: false,
      },
      {
        id: 'offer-decision',
        label: 'Offer Decision',
        description: 'Final hiring decision',
        completed: false,
        active: false,
      },
    ];
  };

  // Filter only accepted applications
  const acceptedApplications = applicationsData.filter(app => app.status === 'accepted');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: 2, bgcolor: 'grey.200' }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ width: '60%', height: 28, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ width: '40%', height: 20, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ width: '80%', height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (acceptedApplications.length === 0) {
    return (
      <Card 
        sx={{ 
          borderRadius: 3, 
          boxShadow: 2, 
          textAlign: 'center', 
          py: 6,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }}
      >
        <CardContent>
          <CheckIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main', 
              mb: 2,
              opacity: 0.7
            }} 
          />
          <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
            No Accepted Applications Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Accepted job applications will appear here. Start reviewing applications to find your next great hire! 🎯
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {acceptedApplications.map((application, index) => (
        <motion.div
          key={application.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.1 }}
        >
          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: 3,
              border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              '&:hover': { 
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {/* Job/Company Avatar */}
                <Avatar
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    fontSize: '2rem'
                  }}
                >
                  {application.product?.images?.[0] ? (
                    <Box
                      component="img"
                      src={application.product.images[0]}
                      alt={application.product.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <WorkIcon sx={{ fontSize: '2rem' }} />
                  )}
                </Avatar>

                {/* Application Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                        {application.product?.name || application.title || 'Job Application'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Accepted on {new Date(application.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<CheckIcon />}
                        label="ACCEPTED"
                        color="success"
                        sx={{ 
                          fontWeight: 'bold',
                          px: 1,
                          '& .MuiChip-icon': {
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                      {application.ai_application && (
                        <Chip 
                          label="AI Generated" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            fontWeight: 'bold',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '& .MuiChip-label': {
                              fontSize: '0.75rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Applicant Information */}
                  {application.applicant && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="body1" fontWeight="600" color="primary.main">
                        {application.applicant.first_name || application.applicant.last_name 
                          ? `${application.applicant.first_name || ''} ${application.applicant.last_name || ''}`.trim() 
                          : 'Anonymous Applicant'
                        }
                      </Typography>
                      {application.applicant.alias && (
                        <Chip 
                          size="small" 
                          label={`@${application.applicant.alias}`} 
                          variant="outlined" 
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Application Info */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        Application submitted: {new Date(application.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Cover Letter Preview */}
                  {application.letter && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4
                      }}>
                        "{application.letter.substring(0, 150)}{application.letter.length > 150 ? '...' : ''}"
                      </Typography>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<ViewIcon />}
                      onClick={() => onViewApplication?.(application)}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                        },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3
                      }}
                    >
                      View Application
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PersonIcon />}
                      onClick={() => onContactApplicant?.(application)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        borderColor: 'success.main',
                        color: 'success.main',
                        '&:hover': {
                          borderColor: 'success.dark',
                          backgroundColor: alpha(theme.palette.success.main, 0.1)
                        }
                      }}
                    >
                      Contact Candidate
                    </Button>
                    {onViewPosting && (
                      <Button
                        variant="outlined"
                        startIcon={<WorkIcon />}
                        onClick={() => onViewPosting(application)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          px: 3,
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        View Job Posting
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => application.meeting ? setViewMeetingOpenForId(application.id) : setManageOpenForId(application.id)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        borderColor: application.meeting ? 'primary.main' : 'secondary.main',
                        color: application.meeting ? 'primary.main' : 'secondary.main',
                        '&:hover': {
                          borderColor: application.meeting ? 'primary.dark' : 'secondary.dark',
                          backgroundColor: application.meeting 
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.secondary.main, 0.08)
                        }
                      }}
                    >
                      {application.meeting ? 'View Schedule' : 'Manage next steps'}
                    </Button>
                  </Box>

                  {/* Interview Progress Steps */}
                  <Box sx={{ mt: 3 }}>
                    <InterviewProgressSteps
                      steps={getInterviewSteps(application)}
                      currentStep={getInterviewSteps(application).findIndex(step => step.active)}
                      variant="default"
                      onStepClick={(stepId, stepIndex) => {
                        const step = getInterviewSteps(application)[stepIndex];
                        if (step.meetingData) {
                          setViewMeetingOpenForId(application.id);
                        } else {
                          console.log('Step clicked:', stepId, stepIndex);
                          // Handle step click - could open a modal or navigate to step details
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {manageOpenForId === application.id && (
            <ManageInterviewModal
              open
              onClose={() => setManageOpenForId(null)}
              onMeetingCreated={handleMeetingCreated}
              application={application}
            />
          )}
          {viewMeetingOpenForId === application.id && (
            <ViewMeetingModal
              open
              onClose={() => setViewMeetingOpenForId(null)}
              meeting={application.meeting}
              application={application}
            />
          )}
        </motion.div>
      ))}
    </Box>
  );
};

export default CompanyAcceptedApplications;
